import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  DataType,
  DropdownStyle,
  InputTypeahead,
  ListItem,
  ReferencesItem,
} from '../../store/typeahead/types';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Entity } from '../../store/view/types';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  Observable,
  startWith,
} from 'rxjs';
import { TypeaheadApiService } from '../../store/typeahead/typeahead.api.service';
import { filter, map, take, tap } from 'rxjs/operators';
import { NgSelectComponent } from '@ng-select/ng-select';
import { AddTagFn } from '../../../common/ng-select/ng-select.module';

@UntilDestroy()
@Component({
  selector: 'app-multi-select-dropdown',
  templateUrl: './multi-select-dropdown.component.html',
  styleUrls: ['./multi-select-dropdown.component.scss'],
  standalone: false,
})
export class MultiSelectDropdownComponent implements OnInit, AfterViewInit {
  @Input() dbNameOfName: string = 'name';
  @Input() dbNameOfId: string = 'id';
  @Input() orderBy: string;
  //Look like a Dropdown or a DropdownList with an input search
  @Input() dropdownStyle: DropdownStyle = DropdownStyle.DropDownList;
  @Input() domain: string = '';
  @Input() pageSize: number = 10;
  @Input() entity: Entity;
  //Limit values from/to a list of a dropdown
  @Input() limitToList: boolean = false;
  @Input() routeEndpoint: string;
  @Input() allowCustomActions: boolean = false;
  @Input() readonly: boolean = false;

  @Input() referenceEndpoint: string;
  @Input() quickAdd: boolean | AddTagFn = false;
  @Input() addTagText: string = 'TYPEAHEAD_LBL_ADD_TAG_TEXT';
  @Input() quickAddRouteEndpoint: string;

  @Input() valueMember: string = 'id';
  @Input() displayMember: string = 'name';
  private usedMember: string = 'name';

  @Input() dataType: DataType = DataType.ReferenceData;

  @Input() showFirstElement: boolean = false;
  private isNewValue: boolean = false;

  @Input() controlForm: any;
  @Input() placeholder: string = '';

  //dev
  // @Input() resultObject: boolean = false;
  @Output() dataSourceEmitter = new EventEmitter<ListItem[]>();

  @ViewChild('select') ngSelect: NgSelectComponent;

  protected readonly DropdownStyle = DropdownStyle;
  dataSource$: Observable<ListItem[] | ReferencesItem[]>;
  searchFilter: string;
  filter: string;
  searchInput$ = new BehaviorSubject<string>('');
  termSearchString: string = '';
  allowEdit: boolean = true;

  searchResult$ = new BehaviorSubject<ListItem[]>([]);

  private isUpdated: boolean = false;
  private itemsUpdated: any;
  private latestItems: (ListItem | ReferencesItem)[] = [];

  initValues$ = new BehaviorSubject<ListItem[]>([]);

  constructor(private readonly typeaheadApiService: TypeaheadApiService) {}

  ngAfterViewInit(): void {
    // console.log(this.controlForm);
  }

  ngOnInit(): void {
    this.searchInput$
      .pipe(
        filter((term) => term !== null),
        // distinctUntilChanged(),
        debounceTime(200),
        // tap((x) => console.log(this.routeEndpoint)),
        // tap((x) => console.log(x)),
        tap((term) => (this.termSearchString = term)),
      )
      .subscribe((termSearchString) => {
        this.searchFilter = '';
        if (termSearchString.length > 0) {
          this.searchFilter = this.dbNameOfName + '=*' + termSearchString;
        }
        let filter: string = '';
        if (this.searchFilter) filter = this.searchFilter;
        if (this.filter) filter = this.filter;
        if (this.searchFilter && this.filter)
          filter = this.searchFilter + ',' + this.filter;

        let inputTypeahead: InputTypeahead = {
          endpoint: this.routeEndpoint,
          orderBy: this.orderBy,
          pageSize: this.pageSize,
          domain: this.domain,
          filter: filter,
        };
        this.typeaheadApiService
          .get(inputTypeahead)
          .pipe(take(1))
          .subscribe((typeahead) => {
            this.searchResult$.next(typeahead);
            this.dataSourceEmitter.emit(typeahead);
          });
      });

    this.dataSource$ = combineLatest([
      this.searchResult$.pipe(startWith([<ListItem>{}])),
      this.initValues$,
    ]).pipe(
      map(([results, initValues]) => {
        const merged = [...results];
        if (initValues && initValues.length > 0) {
          for (const initValue of initValues) {
            const isInArray = merged.some(
              (x) =>
                x.id === initValue.id &&
                x.value === initValue.value &&
                x.name === initValue.name,
            );
            if (!isInArray) {
              merged.push(initValue);
            }
          }
        }
        return merged;
      }),
      tap((items) => (this.latestItems = items)),
    );

    if (this.routeEndpoint) {
      if (!this.limitToList) {
        this.quickAdd = (term) => {
          this.quickAddFn(term);
        };
      }

      this.controlForm?.valueChanges
        .pipe(startWith(this.controlForm?.value))
        .subscribe((value) => {
          this.resolveLabelsIfNeeded(value);
          this.searchInput$.next('');
        });
    }
  }

  quickAddFn(term: string) {
    const fullRouteEndPoint: string = this.quickAddRouteEndpoint
      ? this.routeEndpoint + '/' + this.quickAddRouteEndpoint
      : this.routeEndpoint;
    this.typeaheadApiService
      .quickAdd({
        name: term,
        endpoint: fullRouteEndPoint,
      })
      .pipe(
        filter((x) => !!x && !!this.routeEndpoint),
        take(1),
      )
      .subscribe((item) => {
        if (item != undefined) {
          // console.log(item.id)

          //Add value to show the label
          let initValues = this.initValues$.getValue();
          let cloned = [...initValues, item];
          this.initValues$.next(cloned);

          //Add value in form
          let formValues: any[] = this.controlForm.value;
          if (!formValues) formValues = [];
          formValues = [...formValues, item[this.valueMember]];
          this.controlForm.patchValue(formValues);

          this.searchInput$.next('');
          this.closeSelect(this.ngSelect);
        }
      });
  }

  closeSelect(select: NgSelectComponent) {
    select.close();
  }

  onAdd(event: any) {
    if (this.searchInput$.getValue()) {
      this.searchInput$.next('');
    }
  }

  private resolveLabelsIfNeeded(value: any): void {
    if (!this.routeEndpoint) return;
    if (!Array.isArray(value) || value.length === 0) return;
    if (typeof value[0] === 'object' && value[0][this.displayMember]) return;

    const unresolved = value.filter((v) => {
      const id = typeof v === 'object' ? v?.[this.valueMember] : v;
      return !this.latestItems.some(
        (item) => item && item[this.valueMember] === id,
      );
    });
    if (unresolved.length === 0) return;

    let filterString = '';
    for (const v of unresolved) {
      const id = typeof v === 'object' ? v?.[this.valueMember] : v;
      if (this.dbNameOfId && id) {
        filterString += this.dbNameOfId + '=' + id + '|';
      }
    }
    if (filterString.endsWith('|')) {
      filterString = filterString.substring(0, filterString.length - 1);
    }
    if (!filterString) return;

    const inputTypeahead: InputTypeahead = {
      endpoint: this.routeEndpoint,
      orderBy: this.orderBy,
      pageSize: 25,
      domain: '',
      filter: filterString,
    };

    this.typeaheadApiService
      .get(inputTypeahead)
      .pipe(take(1))
      .subscribe((items) => {
        const current = this.initValues$.getValue();
        const merged = [...current];
        for (const item of items) {
          const exists = merged.some(
            (x) => x[this.valueMember] === item[this.valueMember],
          );
          if (!exists) merged.push(item);
        }
        this.initValues$.next(merged);
      });
  }

  selectedFn = (item: any, selected: any) => {
    if (!item) return false;
    const itemId = item[this.valueMember];
    if (itemId === undefined || itemId === null) return false;
    const selectedId =
      selected !== null && typeof selected === 'object'
        ? selected?.[this.valueMember]
        : selected;
    return itemId === selectedId;
  };

  // onValueMemberChange(newValue: any) {
  //   if (this.resultObject) {
  //     this.controlForm.setValue(newValue ? newValue : undefined);
  //   }
  // }
  onClear() {
    this.searchInput$.next('');
  }
}
